import { createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of, concat, from } from "rxjs";
import { exhaustMap, takeUntil, filter, map, retryWhen, catchError, switchMap, mergeMap } from "rxjs/operators";
import { genericRetryStrategy } from "@nxplatform/helpers/common/retry-strategy";
import { customerAuthProvider } from "@nxplatform/helpers/constants/customer";
import { AuthProvidersList, FetchingStatusess } from "@nxplatform/platform-entities";
import { CustomerBasicAside, CustomerBasicModal, RegistrationStepForm } from "@nxplatform/platform-entities/customer/customer-enum-types";
import { getSignInStart, clearConfirmationError, clearSignInData, getSignInSuccess, clearSignInError } from "@nxplatform/store-shared/authentication/shared-authentication-actions";
import { AUTHENTICATION_FEATURE_KEY } from "@nxplatform/store-shared/authentication/shared-authentication-feature-keys";
import { getAuthenticationState } from "@nxplatform/store-shared/authentication/shared-authentication-selectors";
import { getCustomerTokenSuccess } from "@nxplatform/store-shared/customer/shared-customer-actions";
import { AppStorage } from "@service/api-client/http/helpers/AppStorage";
import { customerAuthTokenName, customerAuthData } from "@service/api-client/token/token-action";
const eparakstsBackPathParamName = "eparaksts_back_path";
const eparakstsIsRegistrationParamName = "eparaksts_is_registration";
const initialAuthenticationState = {
  entities: AppStorage.get(customerAuthData) || null,
  loaded: !!AppStorage.get(customerAuthTokenName),
  authToken: AppStorage.get(customerAuthTokenName) || null,
  signInData: {
    [RegistrationStepForm.email]: { valid: null, value: null },
    [RegistrationStepForm.phone]: { valid: null, value: null },
    [RegistrationStepForm.password]: { valid: null, value: null },
    [RegistrationStepForm.confirmationCode]: {
      valid: true,
      value: null,
      excluded: true
    }
  },
  failed: null,
  error: null,
  errorData: null,
  confirmationFailed: false,
  loading: false,
  eParakstsAuthStatus: FetchingStatusess.none,
  isEparakstsRegistration: !!sessionStorage.getItem(
    eparakstsIsRegistrationParamName
  ),
  balticStatesProviderSession: null,
  confirmEmailError: null,
  openedBasicModal: CustomerBasicModal.none,
  openedBasicAside: CustomerBasicAside.none,
  confirmationTokenLoading: false,
  confirmationToken: null,
  confirmation: null,
  authProvider: AppStorage.get(customerAuthProvider) || AuthProvidersList.EMAIL
};
const authenticationSlice = createSlice({
  name: AUTHENTICATION_FEATURE_KEY,
  initialState: initialAuthenticationState,
  reducers: {
    signInViaBalticStatesProviderStart: (state, action) => {
    },
    signInViaBalticStatesProviderSuccess: (state) => {
    },
    signInViaBalticStatesProviderFail: (state, action) => {
    },
    startBalticStatesProviderSessionStart: (state, action) => {
    },
    startBalticStatesProviderSessionFail: (state, action) => {
    },
    checkBalticStatesProviderAuthStart: (state) => {
    },
    checkBalticStatesProviderAuthSuccess: (state, payload) => {
    },
    startBalticStatesProviderSessionSuccess: (state, action) => {
      state.balticStatesProviderSession = action.payload;
    },
    clearBalticStatesProviderSession: (state) => {
      state.balticStatesProviderSession = null;
    },
    getUserConfirmationStart: (state) => {
      state.confirmationTokenLoading = true;
    },
    getUserConfirmationSuccess: (state, action) => {
      state.confirmationToken = action.payload.token;
      state.confirmationTokenLoading = false;
    },
    getUserConfirmationFailure: (state, action) => {
      state.confirmEmailError = action.payload.message;
      state.confirmationTokenLoading = false;
    },
    customerConfirmationStart: (state, action) => {
    },
    customerConfirmationSuccess: () => {
    },
    customerConfirmationFailure: (state, action) => {
      state.confirmation = action.payload;
    },
    authViaEparakstsStart: (state) => {
      state.eParakstsAuthStatus = FetchingStatusess.progress;
    },
    authViaEparakstsSuccess: (state, payload) => {
      state.eParakstsAuthStatus = FetchingStatusess.success;
    },
    authViaEparakstsErrror: (state, payload) => {
      state.eParakstsAuthStatus = FetchingStatusess.error;
    },
    getEparakstsRegistrationDataSuccess: (state, payload) => {
      state.eParakstsAuthStatus = FetchingStatusess.success;
    },
    resetEparakstsAuthStatus: (state) => {
      state.eParakstsAuthStatus = FetchingStatusess.none;
    },
    createEparakstsSessionStart: (state) => {
    },
    createEparakstsRegistrationSessionStart: (state, payload) => {
      state.isEparakstsRegistration = true;
    },
    createEparakstsSessionSuccess: (state, payload) => {
    },
    createEparakstsSessionErrror: (state, payload) => {
    },
    getSignInFailure: (state, action) => {
      var _a;
      if (action.payload && "messages" in action.payload) {
        state.error = action.payload.messages;
        state.errorData = action.payload.data;
        state.loading = false;
      }
      if ((_a = action.payload) == null ? void 0 : _a.message) {
        state.error = [action.payload.message];
        state.errorData = {
          ...state.errorData,
          messages: [action.payload.data]
        };
        state.loading = false;
      }
    },
    confirmationCodeStart: (state) => {
      state.confirmationFailed = false;
    },
    confirmationCodeSuccess: (state, action) => {
      state.confirmationFailed = false;
      state.error = initialAuthenticationState.error;
    },
    confirmationCodeFailed: (state, action) => {
      state.confirmationFailed = true;
      state.error = [action.payload.message];
      state.errorData = action.payload.data;
    },
    saveSignInData: (state, action) => {
      state.signInData = action.payload;
    },
    setAuthProvider: (state, action) => {
      state.authProvider = action.payload;
      AppStorage.set(customerAuthProvider, action.payload);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getSignInStart, (state, action) => {
      state.error = initialAuthenticationState.error;
      state.loaded = false;
      state.loading = true;
    });
    builder.addCase(clearConfirmationError, (state) => {
      state.error = initialAuthenticationState.error;
      state.errorData = initialAuthenticationState.errorData;
    });
    builder.addCase(clearSignInData, (state) => {
      state.signInData = initialAuthenticationState.signInData;
    });
    builder.addCase(getSignInSuccess, (state, action) => {
      state.loading = false;
    });
    builder.addCase(clearSignInError, (state) => {
      state.error = initialAuthenticationState.error;
      state.confirmationFailed = false;
    });
  }
});
const authenticationReducer = authenticationSlice.reducer;
const {
  checkBalticStatesProviderAuthStart,
  authViaEparakstsErrror,
  createEparakstsRegistrationSessionStart,
  authViaEparakstsStart,
  authViaEparakstsSuccess,
  createEparakstsSessionErrror,
  createEparakstsSessionStart,
  clearBalticStatesProviderSession,
  createEparakstsSessionSuccess,
  customerConfirmationStart,
  customerConfirmationFailure,
  customerConfirmationSuccess,
  getUserConfirmationStart,
  checkBalticStatesProviderAuthSuccess,
  getUserConfirmationFailure,
  getUserConfirmationSuccess,
  getEparakstsRegistrationDataSuccess,
  startBalticStatesProviderSessionFail,
  startBalticStatesProviderSessionStart,
  resetEparakstsAuthStatus,
  signInViaBalticStatesProviderFail,
  signInViaBalticStatesProviderStart,
  startBalticStatesProviderSessionSuccess,
  signInViaBalticStatesProviderSuccess,
  confirmationCodeStart,
  getSignInFailure,
  confirmationCodeSuccess,
  confirmationCodeFailed,
  saveSignInData,
  setAuthProvider
} = authenticationSlice.actions;
const selectSignInEntities = createSelector(
  getAuthenticationState,
  (s) => s.entities
);
const selectSignInToken = createSelector(
  getAuthenticationState,
  (s) => s.authToken
);
const selectSignInData = createSelector(
  getAuthenticationState,
  (s) => s.signInData
);
const selectSignInLoaded = createSelector(
  getAuthenticationState,
  (s) => s.loaded
);
const selectLoginError = createSelector(getAuthenticationState, (s) => s.error);
const selectLoginErrorData = createSelector(
  getAuthenticationState,
  (s) => s.errorData
);
const selectBlueMFailed = createSelector(
  getAuthenticationState,
  (s) => s.failed
);
const selectSignInLoading = createSelector(
  getAuthenticationState,
  (s) => s.loading
);
const selectBalticStatesProviderSessionData = createSelector(
  getAuthenticationState,
  (s) => s.balticStatesProviderSession
);
const selectCustomerConfirmationTokenLoading = createSelector(
  getAuthenticationState,
  (s) => s.confirmationTokenLoading
);
const selectCustomerEmailConfirmError = createSelector(
  getAuthenticationState,
  (s) => s.confirmEmailError
);
const selectCustomerConfirmationEmail = createSelector(
  getAuthenticationState,
  (s) => s.confirmation
);
const selectActiveProvider = createSelector(
  getAuthenticationState,
  (s) => s.authProvider
);
const sendConfirmationLoginCode = (customer, authType) => ({
  type: confirmationCodeStart.type,
  payload: { customer, authType }
});
const customerConfirmationEmailEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(customerConfirmationStart.type),
  switchMap(() => Api.authentication.postConfirmationInstruction().pipe(
    map(customerConfirmationSuccess),
    catchError((err) => of(customerConfirmationFailure(err)))
  ))
);
const customerConfirmationEmail = () => ({
  type: customerConfirmationStart.type
});
const fetchLoginEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getSignInStart.type),
  switchMap((action) => Api.authentication.customerLogin(action.payload).pipe(
    mergeMap(
      (data) => concat(
        of(data.messages ? getSignInFailure(data) : getCustomerTokenSuccess(data)),
        of(getSignInSuccess(data))
      )
    ),
    catchError((err) => of(getSignInFailure(err.data || err.response)))
  ))
);
const fetchConfirmationCodeEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(confirmationCodeStart.type),
  switchMap((action) => Api.authentication.customerConfirmationCode(action.payload).pipe(
    map((data) => confirmationCodeSuccess(data)),
    catchError((err) => of(confirmationCodeFailed(err.response)))
  ))
);
const loginCustomerViaBalticStatesProvider = (payload) => ({
  type: signInViaBalticStatesProviderStart.type,
  payload
});
const loginCustomerViaBalticStatesProviderEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(signInViaBalticStatesProviderStart.type),
  switchMap(
    (action) => from(Api.authentication.signInViaBalticStatesProvider(action.payload)).pipe(
      map((data) => getCustomerTokenSuccess(data)),
      catchError((err) => of(signInViaBalticStatesProviderFail(err)))
    )
  )
);
const checkBalticStatesProviderAuthEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(checkBalticStatesProviderAuthStart.type),
  exhaustMap((action) => Api.authentication.checkBalticStatesProviderStatus(action.payload).pipe(
    takeUntil(action$.pipe(ofType(clearBalticStatesProviderSession.type))),
    filter(
      (data) => data.isComplete !== false
    ),
    map((data) => checkBalticStatesProviderAuthSuccess({ ...data, ...action.payload })),
    retryWhen(
      genericRetryStrategy({ maxRetryAttempts: 5, scalingDuration: 3e3 })
    ),
    catchError((err) => of(startBalticStatesProviderSessionFail(err)))
  ))
);
const startBalticStatesProviderSession = (payload) => ({
  type: startBalticStatesProviderSessionStart.type,
  payload
});
const startBalticStatesProviderSessionEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(startBalticStatesProviderSessionStart.type),
  switchMap((action) => Api.authentication.startBalticStatesProviderSession({
    personalCode: action.payload.personalCode,
    phone: action.payload.phone,
    provider: action.payload.provider,
    countryCode: action.payload.countryCode
  }).pipe(
    map((data) => startBalticStatesProviderSessionSuccess({
      ...data,
      ...action.payload
    })),
    catchError((err) => of(startBalticStatesProviderSessionFail((err == null ? void 0 : err.data) ? err.data : err)))
  ))
);
const checkBalticStatesProviderAuthComplete = (payload) => ({
  type: checkBalticStatesProviderAuthStart.type,
  payload
});
const clearBalticStatesProviderAction = () => ({
  type: clearBalticStatesProviderSession.type
});
const userConfirmation = (data) => ({
  type: getUserConfirmationStart.type,
  payload: data
});
const userConfirmationEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getUserConfirmationStart.type),
  switchMap((action) => Api.authentication.getUserConfirmation(action.payload).pipe(
    map((data) => getUserConfirmationSuccess(data)),
    catchError((err) => of(getUserConfirmationFailure(err)))
  ))
);
const authViaEparaksts = (params) => ({
  type: authViaEparakstsStart.type,
  payload: params
});
const authViaEparakstsEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(authViaEparakstsStart.type),
  switchMap((action) => Api.authentication.authViaEparaksts(action.payload).pipe(
    map((data) => authViaEparakstsSuccess(data)),
    catchError((error) => of(authViaEparakstsErrror(error)))
  ))
);
const selectEparakstsFetchStatus = createSelector(
  getAuthenticationState,
  (s) => s.eParakstsAuthStatus
);
const selectIsEparakstsRegistration = createSelector(
  getAuthenticationState,
  (s) => s.isEparakstsRegistration
);
const createEparakstsAuthSession = () => ({
  type: createEparakstsSessionStart.type
});
const createEparakstsRegistrationSession = (payload) => ({
  type: createEparakstsRegistrationSessionStart.type,
  payload
});
const createEparakstsSessionEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(
    createEparakstsSessionStart.type,
    createEparakstsRegistrationSessionStart.type
  ),
  switchMap(() => Api.authentication.startEparakstsSession().pipe(
    map((data) => createEparakstsSessionSuccess(data)),
    catchError((error) => of(createEparakstsSessionErrror(error)))
  ))
);
export {
  authViaEparaksts,
  authViaEparakstsEpic,
  authViaEparakstsErrror,
  authViaEparakstsStart,
  authViaEparakstsSuccess,
  authenticationReducer,
  checkBalticStatesProviderAuthComplete,
  checkBalticStatesProviderAuthEpic,
  checkBalticStatesProviderAuthStart,
  checkBalticStatesProviderAuthSuccess,
  clearBalticStatesProviderAction,
  clearBalticStatesProviderSession,
  confirmationCodeFailed,
  confirmationCodeStart,
  confirmationCodeSuccess,
  createEparakstsAuthSession,
  createEparakstsRegistrationSession,
  createEparakstsRegistrationSessionStart,
  createEparakstsSessionEpic,
  createEparakstsSessionErrror,
  createEparakstsSessionStart,
  createEparakstsSessionSuccess,
  customerConfirmationEmail,
  customerConfirmationEmailEpic,
  customerConfirmationFailure,
  customerConfirmationStart,
  customerConfirmationSuccess,
  eparakstsBackPathParamName,
  eparakstsIsRegistrationParamName,
  fetchConfirmationCodeEpic,
  fetchLoginEpic,
  getEparakstsRegistrationDataSuccess,
  getSignInFailure,
  getUserConfirmationFailure,
  getUserConfirmationStart,
  getUserConfirmationSuccess,
  initialAuthenticationState,
  loginCustomerViaBalticStatesProvider,
  loginCustomerViaBalticStatesProviderEpic,
  resetEparakstsAuthStatus,
  saveSignInData,
  selectActiveProvider,
  selectBalticStatesProviderSessionData,
  selectBlueMFailed,
  selectCustomerConfirmationEmail,
  selectCustomerConfirmationTokenLoading,
  selectCustomerEmailConfirmError,
  selectEparakstsFetchStatus,
  selectIsEparakstsRegistration,
  selectLoginError,
  selectLoginErrorData,
  selectSignInData,
  selectSignInEntities,
  selectSignInLoaded,
  selectSignInLoading,
  selectSignInToken,
  sendConfirmationLoginCode,
  setAuthProvider,
  signInViaBalticStatesProviderFail,
  signInViaBalticStatesProviderStart,
  signInViaBalticStatesProviderSuccess,
  startBalticStatesProviderSession,
  startBalticStatesProviderSessionEpic,
  startBalticStatesProviderSessionFail,
  startBalticStatesProviderSessionStart,
  startBalticStatesProviderSessionSuccess,
  userConfirmation,
  userConfirmationEpic
};
